/**
 * EvidenceFindingsWrapper
 * Thin layout wrapper for evidence-phase findings routes inside AssessmentShell.
 * Renders EvidenceTrackSidebar (caller-selected track active, Findings sub-item
 * indicated) alongside an Outlet for the findings page content.
 *
 * Symmetric with ReportPhaseWrapper + DimensionNav for the report phase.
 * AssessmentShell handles breadcrumb, PhaseBar, and assessment loading.
 *
 * Clicking a track in the sidebar navigates to the evidence collection layout
 * for that assessment (back to /assessments/:id with the track highlighted).
 */

import { Outlet, useParams, useNavigate } from "react-router-dom";
import { useAssessment } from "@/hooks/useAssessment";
import { EvidenceTrackSidebar } from "@/components/evidence/EvidenceTrackSidebar";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import type { ActiveTrack } from "@/layouts/EvidenceCollectionLayout";

/**
 * Tracks that have a standalone findings page during evidence collection.
 * Direct literal union (not `Extract<ActiveTrack, ...>`) so a rename in
 * ActiveTrack surfaces as a compile error here rather than silently narrowing
 * this type to whatever happens to remain. The `EvidenceTrackSidebar`'s
 * `activeTrack: ActiveTrack` prop downstream already enforces that every
 * FindingsTrack member is a valid ActiveTrack.
 */
type FindingsTrack = "infrastructure" | "codeScan";

interface EvidenceFindingsWrapperProps {
  /**
   * Which evidence track owns the findings page being rendered. Drives sidebar
   * highlight. Owned by routes.tsx because the URL → track mapping is a
   * routing concern, not state derivable from this component.
   */
  activeTrack: FindingsTrack;
}

/**
 * Wrapper that provides EvidenceTrackSidebar for evidence-phase findings routes.
 * The caller-supplied track is preselected and a "Findings" sub-item is shown
 * beneath it. Rendered as a child of AssessmentShell's flex container.
 *
 * @param props - Component props
 * @param props.activeTrack - Track key to highlight in the sidebar
 * @returns Fragment containing EvidenceTrackSidebar and scrollable content Outlet
 */
export function EvidenceFindingsWrapper({
  activeTrack,
}: Readonly<EvidenceFindingsWrapperProps>): JSX.Element {
  const { id: assessmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { assessment, isLoading, refetch } = useAssessment(assessmentId ?? "");

  if (!assessmentId) {
    return (
      <div className="p-8 text-center text-destructive">
        Missing assessment ID.
      </div>
    );
  }

  if (isLoading || !assessment) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" label="Loading assessment..." />
      </div>
    );
  }

  const trackStatuses = assessment?.trackStatuses ?? undefined;

  /**
   * When the user clicks a track in the sidebar, navigate back to the
   * evidence collection view for that assessment. EvidenceCollectionLayout
   * manages track selection via local state, so we simply return to the
   * assessment root and let it default to the overview.
   *
   * @param _track - The selected track key (unused; navigation goes to root)
   */
  const handleTrackSelect = (_track: ActiveTrack) => {
    navigate(`/assessments/${assessmentId}`, { replace: false });
  };

  return (
    <>
      <EvidenceTrackSidebar
        trackStatuses={trackStatuses}
        activeTrack={activeTrack}
        onTrackSelect={handleTrackSelect}
        assessmentId={assessmentId}
        onRefetch={refetch}
        activeSubItem="Findings"
      />
      <div className="flex-1 overflow-y-auto px-6 lg:px-8 py-6 lg:py-8">
        <div className="max-w-5xl mx-auto">
          <Outlet />
        </div>
      </div>
    </>
  );
}
